// =============================================================================
// PROVEEDOR DE TEMA - SISTEMA DE DISEÑO TADMIN
// =============================================================================
// Proveedor de tema que permite cambiar entre tema claro y oscuro
// utilizando los tokens del sistema de diseño

import React, { createContext, useContext, useEffect } from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import designTokens, { shadcnTokens, navigationTokens, tabsTokens } from './tokens/index.js';
import GlobalStyles from './globalStyles.js';
import { safeLocalStorage } from '../lib/storage.js';

// =============================================================================
// CONTEXTO DE TEMA
// =============================================================================

const ThemeContext = createContext({
  theme: 'light',
  toggleTheme: () => {},
  setTheme: () => {},
});

// =============================================================================
// CONFIGURACIÓN DE TEMAS
// =============================================================================

/**
 * Neutrales del Design System para el tema de styled-components.
 *
 * POR QUÉ EXISTE ESTE OVERRIDE
 * ----------------------------
 * `designTokens.colors.light` es la escala MUI/Tailwind heredada, y
 * `GlobalStyles` la usa para pintar `body`, `h1-h6`, `p`, `a` e `input` — o sea,
 * TODO elemento desnudo de la app. El DS solo estiliza clases opt-in (`.y-h1`,
 * `.y-body`), así que nunca le disputaba esos selectores: el resultado era la
 * app entera con texto #111827 (gray-900 de Tailwind) en vez del Negro Apple
 * #1A1A1A del Brand Book, y fondos #F9FAFB en vez de #F7F7F7.
 *
 * Se apunta a las CSS vars del DS en vez de copiar los hex: así un cambio en el
 * DS se propaga sin tocar este archivo.
 *
 * `primary` NO se toca a propósito: `globalStyles.js:176` hace
 * `${...primary.light}33` para el alfa del focus ring, y un `var()` ahí produce
 * `var(--x)33`, que es CSS inválido. Además `primary.main`/`primary.dark` ya
 * traen los valores del DS (#007D6E / #006459).
 *
 * @see docs/design-system-unico.md
 */
const neutralesDS = {
  text: {
    primary:   'var(--yool-black)',   // era #111827 (gray-900)
    secondary: 'var(--yool-gray1)',   // era #4B5563
    disabled:  'var(--yool-muted)',   // era #9CA3AF
    inverse:   'var(--white)',
  },
  background: {
    default: 'var(--white)',
    level1:  'var(--yool-page)',      // era #F9FAFB
    level2:  'var(--yool-gray)',      // era #F3F4F6
  },
  border: {
    light:  'var(--yool-border)',     // era #F3F4F6
    medium: 'var(--yool-border)',     // era #D1D5DB
    dark:   'var(--yool-gray1)',      // antes undefined
  },
};

// Tema claro
const lightTheme = {
  name: 'light',
  colors: {
    ...designTokens.colors.light,
    ...neutralesDS,
  },
  typography: designTokens.typography,
  spacing: designTokens.spacing,
  shadows: designTokens.shadows.base,
  borders: {
    ...designTokens.borders,
    colors: designTokens.borders.colors,
  },
  components: {
    button: designTokens.button,
    input: designTokens.input,
    card: designTokens.card,
    payment: designTokens.payment,
  },
  // Agregar tokens faltantes para componentes
  breadcrumb: designTokens.breadcrumb,
  sidebar: designTokens.sidebar,
  dashboard: designTokens.dashboard,
  loadingSpinner: designTokens.loadingSpinner,
  errorMessage: designTokens.errorMessage,
  layout: designTokens.layout,
  select: designTokens.select,
  form: designTokens.form,
  field: designTokens.field,
  fileUpload: designTokens.fileUpload,
  // Exponer tokens de navegación incluyendo tabs
  navigation: {
    ...navigationTokens,
    tabs: tabsTokens,
  },
  // Integración con shadcn/ui
  shadcn: shadcnTokens,
  // Variables CSS para shadcn/ui
  cssVariables: {
    '--background': '0 0% 100%',
    '--foreground': '240 10% 3.9%',
    '--card': '0 0% 100%',
    '--card-foreground': '240 10% 3.9%',
    '--popover': '0 0% 100%',
    '--popover-foreground': '240 10% 3.9%',
    '--primary': '240 5.9% 10%',
    '--primary-foreground': '0 0% 98%',
    '--secondary': '240 4.8% 95.9%',
    '--secondary-foreground': '240 5.9% 10%',
    '--muted': '240 4.8% 95.9%',
    '--muted-foreground': '240 3.8% 46.1%',
    '--accent': '240 4.8% 95.9%',
    '--accent-foreground': '240 5.9% 10%',
    '--destructive': '0 84.2% 60.2%',
    '--destructive-foreground': '0 0% 98%',
    '--border': '240 5.9% 90%',
    '--input': '240 5.9% 90%',
    '--ring': '240 5.9% 10%',
    '--radius': '0.5rem',
  },
};

// Tema oscuro
const darkTheme = {
  name: 'dark',
  colors: designTokens.colors.dark,
  typography: designTokens.typography,
  spacing: designTokens.spacing,
  shadows: designTokens.shadows.dark,
  borders: {
    ...designTokens.borders,
    colors: designTokens.borders.darkColors,
  },
  components: {
    button: {
      ...designTokens.button,
      variants: {
        ...designTokens.button.variants,
        primary: {
          ...designTokens.button.variants.primary,
          backgroundColor: designTokens.colors.dark.primary.main,
          color: designTokens.colors.dark.primary.contrast,
          '&:hover': {
            backgroundColor: designTokens.colors.dark.primary.dark,
          },
        },
        secondary: {
          ...designTokens.button.variants.secondary,
          backgroundColor: designTokens.colors.dark.secondary.main,
          color: designTokens.colors.dark.secondary.contrast,
        },
        outline: {
          ...designTokens.button.variants.outline,
          color: designTokens.colors.dark.primary.main,
        },
        ghost: {
          ...designTokens.button.variants.ghost,
          color: designTokens.colors.dark.text.primary,
        },
      },
    },
    input: {
      ...designTokens.input,
      states: {
        ...designTokens.input.states,
        default: {
          ...designTokens.input.states.default,
          backgroundColor: designTokens.colors.dark.background.default,
          color: designTokens.colors.dark.text.primary,
        },
      },
    },
    card: {
      ...designTokens.card,
      variants: {
        ...designTokens.card.variants,
        default: {
          ...designTokens.card.variants.default,
          backgroundColor: designTokens.colors.dark.surface.default,
        },
        elevated: {
          ...designTokens.card.variants.elevated,
          backgroundColor: designTokens.colors.dark.surface.elevated,
        },
        interactive: {
          ...designTokens.card.variants.interactive,
          backgroundColor: designTokens.colors.dark.surface.default,
        },
      },
    },
    payment: {
      ...designTokens.payment,
      paymentCard: {
        ...designTokens.payment.paymentCard,
        default: {
          ...designTokens.payment.paymentCard.default,
          backgroundColor: designTokens.colors.dark.surface.default,
        },
      },
      paymentForm: {
        ...designTokens.payment.paymentForm,
        container: {
          ...designTokens.payment.paymentForm.container,
          backgroundColor: designTokens.colors.dark.surface.default,
        },
      },
    },
  },
  // Agregar tokens faltantes para componentes (versión oscura)
  breadcrumb: designTokens.breadcrumb,
  sidebar: designTokens.sidebar,
  dashboard: designTokens.dashboard,
  loadingSpinner: designTokens.loadingSpinner,
  errorMessage: designTokens.errorMessage,
  layout: designTokens.layout,
  select: designTokens.select,
  form: designTokens.form,
  field: designTokens.field,
  fileUpload: designTokens.fileUpload,
  // Exponer tokens de navegación incluyendo tabs
  navigation: {
    ...navigationTokens,
    tabs: tabsTokens,
  },
  // Integración con shadcn/ui
  shadcn: shadcnTokens,
  // Variables CSS para shadcn/ui
  cssVariables: {
    '--background': '240 10% 3.9%',
    '--foreground': '0 0% 98%',
    '--card': '240 10% 3.9%',
    '--card-foreground': '0 0% 98%',
    '--popover': '240 10% 3.9%',
    '--popover-foreground': '0 0% 98%',
    '--primary': '0 0% 98%',
    '--primary-foreground': '240 5.9% 10%',
    '--secondary': '240 3.7% 15.9%',
    '--secondary-foreground': '0 0% 98%',
    '--muted': '240 3.7% 15.9%',
    '--muted-foreground': '240 5% 64.9%',
    '--accent': '240 3.7% 15.9%',
    '--accent-foreground': '0 0% 98%',
    '--destructive': '0 62.8% 30.6%',
    '--destructive-foreground': '0 0% 98%',
    '--border': '240 3.7% 15.9%',
    '--input': '240 3.7% 15.9%',
    '--ring': '240 4.9% 83.9%',
    '--radius': '0.5rem',
  },
};

// =============================================================================
// HOOK PARA USAR EL TEMA
// =============================================================================

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme debe ser usado dentro de un ThemeProvider');
  }
  return context;
};

// =============================================================================
// PROVEEDOR DE TEMA
// =============================================================================

export const ThemeProvider = ({ children }) => {
  // El modo oscuro se retiró: tadmin es un panel interno y su UI solo está
  // pulida en claro. El tema queda FIJO en 'light'. Se conservan toggleTheme /
  // setTheme como no-ops para no romper los componentes que aún los importan.
  const currentTheme = 'light';

  // Asegurar que nunca quede la clase .dark aplicada (por sesiones previas que
  // la hubieran guardado en localStorage/DOM antes de retirar el modo oscuro).
  useEffect(() => {
    document.documentElement.classList.remove('dark');
    document.documentElement.setAttribute('data-theme', 'light');
    safeLocalStorage.setItem('tadmin-theme', 'light');
  }, []);

  // No-ops: el tema ya no cambia.
  const toggleTheme = () => {};
  const setTheme = () => {};

  const themeObject = lightTheme;

  // Valor del contexto
  const contextValue = {
    theme: currentTheme,
    toggleTheme,
    setTheme,
    themeObject,
    // Acceso directo a tokens de shadcn/ui
    shadcn: themeObject.shadcn,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <StyledThemeProvider theme={themeObject}>
        <GlobalStyles />
        {children}
      </StyledThemeProvider>
    </ThemeContext.Provider>
  );
};

// =============================================================================
// COMPONENTE PARA CAMBIAR TEMA
// =============================================================================

// El modo oscuro se retiró: el toggle ya no se muestra. Se conserva el export
// como componente vacío para no romper los imports existentes (Layout, etc.).
export const ThemeToggle = () => null;

// =============================================================================
// UTILIDADES DE TEMA
// =============================================================================

// Hook para obtener valores específicos del tema
export const useThemeValue = (path) => {
  const { themeObject } = useTheme();
  
  return path.split('.').reduce((obj, key) => {
    return obj && obj[key] !== undefined ? obj[key] : undefined;
  }, themeObject);
};

// Hook para detectar si es tema oscuro
export const useIsDarkTheme = () => {
  const { theme } = useTheme();
  return theme === 'dark';
};

// Función para obtener colores del tema actual
export const getThemeColors = (theme = 'light') => {
  return theme === 'dark' 
    ? designTokens.colors.dark 
    : designTokens.colors.light;
};

// Función para crear estilos condicionales por tema
export const createThemeStyles = (lightStyles, darkStyles) => {
  return (props) => {
    const isDark = props.theme?.name === 'dark';
    return isDark ? darkStyles : lightStyles;
  };
};

// =============================================================================
// EXPORTACIONES
// =============================================================================

export { lightTheme, darkTheme };
export default ThemeProvider;
